/**
 * /api/app/cms 首页 CMS 接口封装。
 *
 * 类型对齐 contracts/openapi.yaml，
 * 统一经 @/api/client 请求。
 */
import { requestJson } from '@/api/client'
import type { components } from '@/types/generated/api'

type JobCard = components['schemas']['JobCard']
type CmsPageClient = components['schemas']['CmsPageClient']
type CmsPageBrief = components['schemas']['CmsPageBrief']

/** Banner 数据 */
export interface CmsBanner {
  banner_id: number
  title: string
  image_url: string
  link_url?: string
  sort_order: number
  enabled: number
  start_time?: string
  end_time?: string
}

/** 首页导师轮播卡片 */
export interface FeaturedMentor {
  mentorId: number
  displayName: string
  title?: string
  company?: string
  industry?: string
  tagline?: string
  aiAvatar?: string
  avatarFileId?: number
  yearsOfExp?: number
  avgRating: number
  reviewCount: number
}

/** 首页学员评价卡片 */
export interface FeaturedReview {
  reviewId: number
  content?: string
  rating: number
  reviewerName: string
  createdAt?: string
  mentor: {
    mentorId: number
    displayName: string
    title?: string
    company?: string
    aiAvatar?: string
    avatarFileId?: number
  }
}

/** 平台统计数据 */
export interface PlatformStats {
  mentorYearsAvg: number
  mentorCount: number
  countryCount: number
  totalSessions: number
}

/** 企业 Logo */
export interface CompanyLogo {
  name: string
  logo_url?: string
  sort?: number
}

/** 首页活动卡片 */
export interface HomepageEvent {
  eventId: number
  title: string
  description?: string
  coverImageUrl?: string
  eventType: string
  startTime: string
  endTime: string
  durationHours?: number
  timezone?: string
  price: number
  mentorDisplayName?: string
  avgRating: number
  reviewCount: number
}

/** 首页小组讨论卡片 */
export interface HomepageGroupSession {
  sessionId: number
  title: string
  description?: string
  coverImageUrl?: string
  mentorId: number
  mentorDisplayName?: string
  startTime: string
  durationMinutes: number
  maxParticipants: number
  currentParticipants: number
  price: number
  topicTags?: string[]
}

/** 首页聚合数据 */
export interface HomepageData {
  banners: CmsBanner[]
  hero: string | Record<string, string>
  hotCities: string | string[]
  serviceTypes: string | Array<{ label: string; desc: string; icon: string }>
  sectionOrder?: Record<string, string> | string
  sectionSettings?: Record<string, string>
  featuredJobs: JobCard[]
  featuredMentors?: FeaturedMentor[]
  featuredReviews?: FeaturedReview[]
  stats?: PlatformStats
  companyLogos?: CompanyLogo[]
  upcomingEvents?: HomepageEvent[]
  groupSessions?: HomepageGroupSession[]
  becomeMentor?: Record<string, string> | string
  newsletter?: Record<string, string> | string
  aiMentorChat?: Record<string, string> | string
}

/** 页脚栏目链接 */
export interface FooterLink {
  label: string
  url: string
}

/** 页脚栏目 */
export interface FooterColumn {
  title: string
  links: FooterLink[]
}

/** 全站页脚配置（/api/app/cms/footer，值为字符串，columns 为 JSON 串） */
export interface FooterData {
  brand_name?: string
  brand_tagline?: string
  columns?: FooterColumn[] | string
  copyright?: string
  icp?: string
}

/** C 端站点配置（主题配色等） */
export interface SiteConfig {
  theme: string
}

export const cmsApi = {
  /** 一次性返回首页全部 CMS 配置 */
  getHomepage(jobLimit?: number): Promise<HomepageData> {
    return requestJson<HomepageData>({
      method: 'GET',
      url: '/api/app/cms/homepage',
      params: { jobLimit },
    })
  },

  /** 仅返回已启用 Banner 列表 */
  getBanners(): Promise<CmsBanner[]> {
    return requestJson<CmsBanner[]>({
      method: 'GET',
      url: '/api/app/cms/banners',
    })
  },

  /** 获取全站页脚配置（品牌/栏目链接/版权） */
  getFooter(): Promise<FooterData> {
    return requestJson<FooterData>({
      method: 'GET',
      url: '/api/app/cms/footer',
    })
  },

  /** 获取 C 端站点配置（主题配色等） */
  getSiteConfig(): Promise<SiteConfig> {
    return requestJson<SiteConfig>({
      method: 'GET',
      url: '/api/app/cms/site-config',
    })
  },

  /** 已发布站点页面轻量列表（slug/title/sortOrder） */
  listPages(): Promise<CmsPageBrief[]> {
    return requestJson<CmsPageBrief[]>({
      method: 'GET',
      url: '/api/app/cms/pages',
    })
  },

  /** 站点页面详情（仅已发布 + 可见；不存在或已下线时后端返回 HTTP 404） */
  getPage(slug: string): Promise<CmsPageClient> {
    return requestJson<CmsPageClient>({
      method: 'GET',
      url: `/api/app/cms/pages/${encodeURIComponent(slug)}`,
    })
  },
}
